import * as yup from 'yup'
import { CSSProperties, useEffect, useState } from 'react'
import { IOptionsProps, IOptionProps } from '../../../../../@types/reactSelect/IOption'
import { ICoords } from '../../../../../@types/GoogleMaps/ICoord'
import { FooterEditOnboarding } from '../../../../EditOnboarding/Footer'
import { AlertModal } from '../../../../Modal/Alert/Index'
import { SimpleButtonClose } from '../../../../Modal/Contents/SimpleButtonClose/Index'
import { TextModal } from '../../../../Modal/Contents/TextModal/Index'
import { TitleModal } from '../../../../Modal/Contents/TitleModal/Index'
import { ButtonRedirect } from '../../../../Modal/Error/ButtonRedirect/Index'
import { moveCharLeft } from '../../../../../utils/arrays/moveChar'
import { calculateHectares } from '../../../../../utils/geolib/calculateHectares'
import { areaService } from '../../../../../services/areaService'
import { farmService } from '../../../../../services/farmService'
import { userService } from '../../../../../services/userService'
import Constants from '../../../../../constants/Index'
import { useEditOnboarding } from '../../../../../hooks/useEditOnboarding'
import { AreaFormV2, AreaFormValueV2, FieldErrorsV2 } from '../../../../FormsV2'
import { useTranslation } from 'react-i18next'
import { DrawOnMapSlotV2 } from '../../../../OnboardingV2/DrawOnMapSlotV2'
import '../../../styles.css'
import './styles.css'

function toDayOption(value: any): IOptionProps | null {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'object' && 'label' in value && 'value' in value) return value
  return { label: `${value} Dias`, value }
}

function buildAreaFormValue(defaultValue: any, farms?: IOptionsProps, managers?: IOptionsProps): AreaFormValueV2 {
  const farm = farms?.find((option) =>
    option.value === defaultValue?.farm_id ||
    option.info?.initials === defaultValue?.farm_initials
  ) || null
  const manager = managers?.find((option) => option.value === defaultValue?.manager_user_id) ||
    (defaultValue?.manager_user_id
      ? { label: defaultValue?.manager_user_name || String(defaultValue.manager_user_id), value: defaultValue.manager_user_id }
      : null)

  return {
    id: defaultValue?.id,
    uuid: defaultValue?.uuid,
    farm,
    name: defaultValue?.name || '',
    initials: defaultValue?.initials || '',
    acreage: defaultValue?.acreage || '',
    indoor_grazing_time: toDayOption(defaultValue?.indoor_grazing_time),
    outdoor_grazing_time: toDayOption(defaultValue?.outdoor_grazing_time),
    manager_user_id: manager,
    map_coords: defaultValue?.map_coords || [],
  }
}

function toFieldErrors(error: yup.ValidationError) {
  return error.inner.reduce((errors, currentError) => {
    if (currentError.path) errors[currentError.path] = currentError.message
    return errors
  }, {} as FieldErrorsV2)
}

export function Area() {
  const { t } = useTranslation()
  const { setOptionForm, defaultValue } = useEditOnboarding()
  const isNew = !defaultValue
  const [listFarms, setListFarms] = useState<IOptionsProps>()
  const [managersList, setManagersList] = useState<IOptionsProps>()
  const [currentFarm, setCurrentFarm] = useState<any>()
  const [msgError, setMsgError] = useState<string>('')
  const [submiting, setSubmiting] = useState(false)
  const [staticAreas, setStaticAreas] = useState<ICoords[]>([])
  const [remainingArea, setRemainingArea] = useState<string>('')
  const [parentAcreage, setParentAcreage] = useState<string>('')
  const [occupiedArea, setOccupiedArea] = useState<number>(0)
  const [areaV2, setAreaV2] = useState<AreaFormValueV2>(() => buildAreaFormValue(defaultValue, listFarms, managersList))
  const [errorsV2, setErrorsV2] = useState<FieldErrorsV2>({})

  const areaSchemaV2 = yup.object().shape({
    farm: yup.object().nullable().required(t('internalValidation.farmRequired')),
    name: yup.string().required(t('internalValidation.required')),
    initials: yup
      .string()
      .matches(/^\S+(\s+\S+)*$/, t('internalValidation.invalidInitials'))
      .required(t('internalValidation.initialsRequired')),
    acreage: yup
      .string()
      .test('value', t('internalValidation.required'), () => Number(areaV2.acreage?.replace(',', '.') || 0) !== 0)
      .test('value', t('internalValidation.mapAvailableExceeded'), () => Number(remainingArea) >= 0)
      .required(t('internalValidation.required')),
    indoor_grazing_time: yup.object().nullable().required(t('internalValidation.required')),
    outdoor_grazing_time: yup.object().nullable().required(t('internalValidation.required')),
    manager_user_id: yup.object().nullable().required(t('internalValidation.required')),
    map_coords: yup.array().min(1, t('internalValidation.mapAreaRequired')),
  })

  const contentStyleAlert = {
    width: '335px',
    height: '237px',
    borderRadius: '20px',
    padding: '20px',
  } as CSSProperties

  const deleteArea = () => {
    if (defaultValue?.id) {
      areaService
        .deleteArea(defaultValue.id)
        .then(() => setOptionForm(undefined))
        .catch((error) => setMsgError(error.response.data.msg))
    }
  }

  const submitAreaV2 = async () => {
    if (submiting) return

    try {
      await areaSchemaV2.validate(areaV2, { abortEarly: false })
      setErrorsV2({})
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrorsV2(toFieldErrors(error))
      return
    }

    setSubmiting(true)
    const acreageFix = areaV2.acreage?.replace(',', '.')

    if (isNew) {
      const formNewArea = [
        {
          name: areaV2.name,
          initials: areaV2.initials,
          acreage: moveCharLeft(acreageFix, acreageFix.charAt(2)),
          farm_id: areaV2.farm?.value,
          indoor_grazing_time: parseInt(String(areaV2.indoor_grazing_time?.value)),
          outdoor_grazing_time: parseInt(String(areaV2.outdoor_grazing_time?.value)),
          owner_user_id: areaV2.manager_user_id?.value,
          map_coords: areaV2.map_coords,
        },
      ]

      areaService
        .createNewArea(formNewArea)
        .then(() => {
          setOptionForm(undefined)
          setMsgError('')
        })
        .catch((error) => setMsgError(error.response.data.msg))
        .finally(() => setSubmiting(false))
    } else {
      const formEditArea = [
        {
          area_id: defaultValue?.id || -1,
          name: areaV2.name,
          initials: areaV2.initials,
          acreage: moveCharLeft(acreageFix, acreageFix.charAt(2)),
          indoor_grazing_time: parseInt(String(areaV2.indoor_grazing_time?.value)),
          outdoor_grazing_time: parseInt(String(areaV2.outdoor_grazing_time?.value)),
          owner_user_id: parseInt(String(areaV2.manager_user_id?.value)),
        },
      ]

      areaService
        .editArea(formEditArea)
        .then(() => setOptionForm(undefined))
        .catch((error) => setMsgError(error.response.data.msg))
        .finally(() => setSubmiting(false))
    }
  }

  function updateAreaV2(value: AreaFormValueV2) {
    setAreaV2(value)
    if (value.farm?.value !== areaV2.farm?.value) {
      setCurrentFarm(value.farm?.info)
    }
  }

  function updateAreaMapCoords(map_coords: ICoords) {
    const acreage = map_coords.length >= 3 ? calculateHectares(map_coords) : areaV2.acreage
    setAreaV2((current) => ({ ...current, map_coords, acreage }))
  }

  useEffect(() => {
    userService
      .getUsersByOffice(Constants.PROFILE_CODE_CAPATAZ.toString())
      .then((data) => {
        setManagersList(
          data.map((user: any) => ({
            value: user.id,
            label: user.name,
          })),
        )
      })

    // Carregar fazendas sempre (para dropdown)
    farmService.getFarmsByUser().then((data) => {
      setListFarms(
        data.map((farm: any) => ({
          label: farm.name,
          value: farm.id,
          info: farm,
        })),
      )
    })
  }, [defaultValue])

  // Calcula área da fazenda
  useEffect(() => {
    if (currentFarm && currentFarm.map_coords && currentFarm.map_coords.length >= 3) {
      const farmArea = calculateHectares(currentFarm.map_coords)
      setParentAcreage(farmArea)
    } else {
      setParentAcreage('')
      setRemainingArea('')
    }
  }, [currentFarm])

  // Calcula área restante da fazenda quando acreage muda
  useEffect(() => {
    if (parentAcreage) {
      const farmArea = Number(parentAcreage)
      const currentAreaValue = Number(areaV2.acreage?.replace(',', '.') || 0)
      const available = farmArea - occupiedArea - currentAreaValue
      setRemainingArea(available.toFixed(2))
    }
  }, [parentAcreage, areaV2.acreage, occupiedArea])

  useEffect(() => {
    if (currentFarm) {
      // Buscar áreas existentes da farm para exibir no mapa e calcular área ocupada
      areaService.getAreabyFarmId(currentFarm.id).then((data) => {
        // Filtrar áreas para exibir no mapa (excluindo a área sendo editada)
        const otherAreas = data.filter((area) => area.id !== defaultValue?.id)
        setStaticAreas(otherAreas.map((area) => area.map_coords))

        // Calcular área total ocupada
        const acreageUsed = otherAreas
          .reduce((sum, current) => sum + Number(current.acreage), 0)
        setOccupiedArea(acreageUsed)
      })
    } else {
      setStaticAreas([])
      setOccupiedArea(0)
    }
  }, [currentFarm, defaultValue?.id])

  useEffect(() => {
    if (!isNew && defaultValue && listFarms && managersList) {
      const nextValue = buildAreaFormValue(defaultValue, listFarms, managersList)
      setAreaV2(nextValue)
      setCurrentFarm(nextValue.farm?.info)
    }
  }, [defaultValue, isNew, listFarms, managersList])

  const areaPrefix = isNew
    ? currentFarm?.initials
      ? `${currentFarm.initials}-`
      : ''
    : `${defaultValue?.farm_initials}-`

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError('')} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>{t("managementUi.somethingWrong")}</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError('')}>
            Voltar
          </ButtonRedirect>
        </section>
      </AlertModal>
      <main className="body__formEditOnboarding body__formEditOnboarding--property-v2">
        <AreaFormV2
          value={areaV2}
          mode={isNew ? 'create' : 'edit'}
          farms={listFarms || []}
          managers={managersList || []}
          dayOptions={Constants.DAYS_OPTIONS}
          initialsPrefix={areaPrefix}
          autoGenerateInitials={isNew}
          availableArea={remainingArea}
          errors={errorsV2}
          disabledFields={isNew ? [] : ['farm', 'initials', 'acreage']}
          showActions={false}
          showCardHeader={false}
          cardTitle={isNew ? t('formCards.newArea') : t('formsV2.area')}
          cardDescription={
            areaV2.name ? `${areaV2.name}${areaV2.initials ? ` (${areaPrefix}${areaV2.initials})` : ''}` : undefined
          }
          onFarmChange={(farm) => setCurrentFarm(farm?.info)}
          mapSlot={
            <div className="edit-property-v2__map-slot">
              <DrawOnMapSlotV2
                value={areaV2.map_coords as ICoords}
                staticsElements={staticAreas}
                fitCoords={currentFarm?.map_coords || areaV2.farm?.info?.map_coords || []}
                initialsLabel={`${areaPrefix}${areaV2.initials || ''}`}
                editable={isNew && (!!currentFarm || !!areaV2.farm)}
                style={{ width: '100%', height: '240px' }}
                onChange={updateAreaMapCoords}
              />
            </div>
          }
          onChange={updateAreaV2}
        />
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submitAreaV2}
        deleteFunction={isNew ? undefined : deleteArea}
        isNew={isNew}
        disabledSubmit={submiting}
      />
    </>
  )
}
